// Turns SimState + now into the view shapes the screens render. Every
// function here is pure and cheap enough to call on every tick.

import type {
  Chore,
  GameStatus,
  HomeView,
  HubView,
  LeagueCard,
  LeagueRef,
  LineupHealth,
  LineupRowView,
  LineupView,
  MatchupRowView,
  MatchupSide,
  MatchupSummary,
  MatchupView,
  PeriodRef,
  PlayerRowView,
  PlayerSummary,
  PlayersPage,
  PlayersQuery,
  PlayView,
  ProjectionBasis,
  ScoreboardView,
  StandingsRow,
  StandingsView,
  TeamRef,
  WorthALookView,
} from '@fsp/domain';
import {
  CHORE_ORDER,
  HEAD_TO_HEAD,
  toolsRefOf,
  botFace,
  canFillSlot,
  comparePlayerRows,
  noScoreOf,
  playoffStatuses,
  positionNumbersFor,
  worthLists,
  WORTH_WEEKS,
  qualifiesForReserve,
  raceNotes,
  raceSentence,
  ranksWithin,
  slotsWithReserve,
  statsFreshness,
  withGamesBack,
  withPlayoffLine,
  withSeeds,
  withTableLines,
  type TableGame,
} from '@fsp/domain';
import { castMember, matchupLine } from '@fsp/bots';
import { football } from '@fsp/sports-football';
import { foldBreakdown, scorePlayer } from '@fsp/scoring';
import type { StatLine } from '@fsp/sports-core';
import { GAME_SECONDS, clubLabel, gameLabel, gameProgress, lineAt } from './games';
import {
  gameFor,
  pointsFor,
  projectionFor,
  type SimMatchup,
  type SimPlayer,
  type SimState,
  type SimTeam,
} from './league';
import { bestOf, pastWeekPoints, poolDraws, weekPoints } from './past-weeks';
import { pulseFromChat } from './chat';
import { lineupLockAt, lockedPlayers, pendingClaimsSummary, waiverClearsAt } from './waivers';
import { offersForMe } from './trades';
import { myWeek } from './recap';
import { newsItems } from './news';

/** A pro game is live from kickoff until it has run its simulated length. */
const LIVE_MS = GAME_SECONDS * 1000;

const slotDefs = new Map(football.slots.map((s) => [s.id, s]));
const statuses = new Map(football.injuryStatuses.map((s) => [s.id, s]));

function iso(ms: number): string {
  return new Date(ms).toISOString();
}

export function periodRef(state: SimState, period = state.currentPeriod): PeriodRef {
  const offset = (period - state.currentPeriod) * 7 * 24 * 3600 * 1000;
  return {
    index: period,
    label: `Week ${period}`,
    start: iso(state.periodStart + offset),
    end: iso(state.periodEnd + offset),
    isCurrent: period === state.currentPeriod,
    kind: period > state.regularSeasonEnd ? 'playoff' : 'regular',
  };
}

export function playerSummary(state: SimState, p: SimPlayer): PlayerSummary {
  const status = p.injury ? statuses.get(p.injury.status) : null;
  return {
    id: p.id,
    name: p.name,
    shortName: p.shortName,
    positions: [...p.positions],
    proTeam: p.proTeam,
    injury:
      p.injury && status
        ? { status: status.id, name: status.name, severity: status.severity, note: p.injury.note }
        : null,
    onBye: state.byes.includes(p.proTeam),
  };
}

export function gameStatus(state: SimState, p: SimPlayer, now: number): GameStatus {
  const g = gameFor(state, p);
  if (!g) {
    return { state: 'bye', label: 'Bye', opponent: null, home: false, startAt: null, score: null };
  }
  const home = g.home === p.proTeam;
  const { state: st, label, score } = gameLabel(g, now, readerZoneOf(state));
  return {
    state: st,
    label: st === 'pre' ? label : label,
    opponent: home ? g.away : g.home,
    home,
    startAt: iso(g.startAt),
    score,
  };
}

function currentLine(state: SimState, p: SimPlayer, now: number) {
  const g = gameFor(state, p);
  if (!g) return null;
  if (now < g.startAt) return null;
  const events = state.events.get(p.id) ?? [];
  return lineAt(events, gameProgress(g, now));
}

export function livePoints(state: SimState, p: SimPlayer, now: number): number | null {
  const line = currentLine(state, p, now);
  return line ? pointsFor(state, p.id, line) : null;
}

const gameByTeamCache = new WeakMap<
  SimState,
  Map<string, (typeof football.proTeams)[number] extends never ? never : import('./games').ProGame>
>();
function gameByTeam(state: SimState) {
  let m = gameByTeamCache.get(state);
  if (!m) {
    m = new Map();
    for (const g of state.proGames) {
      m.set(g.home, g);
      m.set(g.away, g);
    }
    gameByTeamCache.set(state, m);
  }
  return m;
}

const statNames = new Map(football.stats.map((s) => [s.key, s.name]));

/** What a live score is made of, stat by stat: the same fold the league's rows use. */
function breakdownFor(state: SimState, p: SimPlayer, line: StatLine | null) {
  if (!line || Object.keys(line).length === 0) return null;
  return foldBreakdown(
    scorePlayer([{ gameId: 'g', line }], p.positions, state.scoring).breakdown,
    (key) => statNames.get(key) ?? key,
  );
}

export function projection(state: SimState, p: SimPlayer): number {
  return projectionFor(p, gameByTeam(state), state.byes);
}

/**
 * Where a projection came from, for the Why popover. The adjustment mirrors
 * projectionFor() in league.ts exactly: points per period, times an injury
 * factor, zero on a bye or Out. If that formula changes, change this with it,
 * or the popover starts lying about the number beside it. The average beside
 * it is the season he has had, as Players shows it (`seasonSoFar`).
 */
export function basisFor(state: SimState, p: SimPlayer, game: GameStatus | null): ProjectionBasis {
  const adjustment =
    game?.state === 'bye' || !gameFor(state, p)
      ? 'On bye'
      : p.injury?.status === 'O'
        ? 'Out'
        : p.injury?.status === 'Q'
          ? 'Questionable'
          : null;
  const periods = Math.max(0, state.currentPeriod - 1);
  // THE POOL'S OWN FIGURE: the season points the Players list shows for him,
  // over the games that produced them — the contract's "points per game so
  // far" and the real league's arithmetic (`basisOf`). It was his talent rate,
  // `ppg`, which is what the demo's games are drawn from and not what they
  // came to: Jett Scrimmage's average read 13.3 on My Team beside 40.1 points
  // in two games on Players (the review of round 2, CONTRACT-5). Before period
  // one's games there is nothing to average, and the contract says so with a
  // null.
  const season = seasonSoFar(p, periods);
  return {
    seasonAvg: periods > 0 ? Math.round((season.points / periods) * 100) / 100 : null,
    periods,
    opponent: game?.opponent ? `${game.home ? 'vs' : 'at'} ${clubLabel(game.opponent)}` : null,
    adjustment,
    // The simulator projects everybody, so it never has a reason to give.
    withheld: null,
  };
}

export function teamRef(state: SimState, team: SimTeam, now: number): TeamRef {
  const rec = record(state, team.id, now);
  return {
    id: team.id,
    name: team.name,
    abbreviation: team.abbreviation,
    ownerName: team.ownerName,
    colors: team.colors,
    // A bot's team wears its character's face (B-464), as a real league's
    // bot seat does; a person's wears its initials, the practice league
    // keeping no badge of its own.
    badge:
      team.ai !== null && botFace(team.ai.presetId) !== null
        ? { kind: 'bot', id: team.ai.presetId }
        : null,
    // The practice league keeps no banner either; a bot's shows its own
    // through \`bannerSceneOn\`, from the face above.
    banner: null,
    record: rec,
    isMine: team.isMine,
    titles: titlesFor(state, team.id),
  };
}

/** Championships in this league's history, plus this season's once minted. */
export function titlesFor(state: SimState, teamId: string): number {
  let n = 0;
  for (const season of state.history)
    if (season.teams.some((t) => t.teamId === teamId && t.champion)) n++;
  if (
    state.awards.minted.some(
      (m) =>
        m.awardId === 'champion' && !m.supersededBy && m.winners.some((w) => w.teamId === teamId),
    )
  )
    n++;
  return n;
}

// ── Lineup ────────────────────────────────────────────────────────────────

function lockAtFor(state: SimState, p: SimPlayer | null): number | null {
  // THE LEAGUE'S LOCK RULE, as the league store reads it (`lineups.ts`
  // `lockAtFor`): at the week's first game or a fixed time the whole lineup
  // locks at once, and only `per-player-game` locks each slot at its own
  // kickoff. This read the kickoff alone, so a practice league set to lock at
  // the first game left later slots editable after it (noted 2026-09-25,
  // fixed 2026-09-27). Waivers already held to the rule; the lineup now does.
  if (state.settings.lineup.lockRule !== 'per-player-game') return lineupLockAt(state);
  if (!p) return null;
  const g = gameFor(state, p);
  return g ? g.startAt : null;
}

export function lineupRows(state: SimState, team: SimTeam, now: number): LineupRowView[] {
  const reserve = state.settings.roster.reserve;
  return team.lineup.map((row) => {
    const def = slotDefs.get(row.slot)!;
    const p = row.playerId ? state.players.get(row.playerId)! : null;
    const lockAt = lockAtFor(state, p);
    const game = p ? gameStatus(state, p, now) : null;
    const proj = p ? projection(state, p) : 0;
    const pts = p ? livePoints(state, p, now) : null;
    const line = p ? currentLine(state, p, now) : null;
    let health: LineupRowView['health'] = 'ok';
    if (def.kind === 'starter') {
      if (!p) health = 'empty';
      else if (!def.eligible.includes(p.positions[0])) health = 'illegal';
      else if (game?.state === 'bye') health = 'bye';
      else if (p.injury?.status === 'O') health = 'out';
      else if (p.injury) health = 'warn';
    } else if (
      def.kind === 'reserve' &&
      p &&
      reserve !== null &&
      reserve.mustMoveWhenHealthy &&
      !qualifiesForReserve(reserve, p.injury?.status ?? null)
    ) {
      // The league's rule (D9), not one status: this read `status !== 'O'`.
      health = 'illegal';
    }
    return {
      // TODO(E6): the matchup cue in the tour, which the simulator's Players
      // rows lack too: one week of pro games gives no club a season to rank.
      matchup: null,
      slot: row.slot,
      slotIndex: row.slotIndex,
      slotKind: def.kind,
      player: p ? playerSummary(state, p) : null,
      game,
      projection: proj,
      points: pts,
      locked: lockAt !== null && now >= lockAt,
      lockAt: lockAt ? iso(lockAt) : null,
      eligibleSlots: p
        ? football.slots
            .filter((s) => s.kind === 'starter' && s.eligible.includes(p.positions[0]))
            .map((s) => s.id)
        : [],
      health,
      statLine:
        line && Object.keys(line).length
          ? football.display.summarizeLine(line, p!.positions)
          : null,
      breakdown: p && pts !== null ? breakdownFor(state, p, line) : null,
      basis: p ? basisFor(state, p, game) : null,
      seasonAvg: p ? basisFor(state, p, game).seasonAvg : null,
      reserveEligible:
        p !== null && reserve !== null && qualifiesForReserve(reserve, p.injury?.status ?? null),
      // The practice league has no taxi squad (PLAN-2026-10-27-the-next-season §2.7).
      taxiEligible: false,
      // The simulated leagues set their lineups: no best ball.
      countsAs: null,
    };
  });
}

export function lineupHealth(state: SimState, team: SimTeam, now: number): LineupHealth {
  const rows = lineupRows(state, team, now);
  const starters = rows.filter((r) => r.slotKind === 'starter');
  const bench = rows.filter((r) => r.slotKind === 'bench' && r.player);
  const problems: LineupHealth['problems'] = [];
  const firstLock = starters
    .map((r) => (r.lockAt ? Date.parse(r.lockAt) : null))
    .filter((x): x is number => x !== null && x > now)
    .sort((a, b) => a - b)[0];

  for (const r of starters) {
    if (r.locked) continue;
    // How many bench players could take this slot — not which one. Naming the
    // pick is the manager's job (docs/decisions/2026-09-12-tools-not-instructions.md);
    // ours is to say the slot can be fixed and open the choice.
    const swap = (() => {
      const candidates = bench.filter(
        (b) =>
          b.eligibleSlots.includes(r.slot) &&
          b.health === 'ok' &&
          !b.locked &&
          b.game?.state !== 'bye',
      ).length;
      return candidates > 0 ? { candidates } : null;
    })();
    if (r.health === 'empty')
      problems.push({
        code: 'empty-slot',
        message: `Empty ${slotDefs.get(r.slot)!.name} slot.`,
        rowSlot: r.slot,
        rowSlotIndex: r.slotIndex,
        swap,
        fix: null,
      });
    else if (r.health === 'out')
      problems.push({
        code: 'starter-out',
        message: `${r.player!.shortName} is Out.`,
        rowSlot: r.slot,
        rowSlotIndex: r.slotIndex,
        swap,
        fix: null,
      });
    else if (r.health === 'bye')
      problems.push({
        code: 'starter-bye',
        message: `${r.player!.shortName} is on bye.`,
        rowSlot: r.slot,
        rowSlotIndex: r.slotIndex,
        swap,
        fix: null,
      });
    else if (r.player?.injury?.severity === 'likely-out')
      problems.push({
        code: 'starter-doubtful',
        message: `${r.player.shortName} is Doubtful.`,
        rowSlot: r.slot,
        rowSlotIndex: r.slotIndex,
        swap,
        fix: null,
      });
  }
  // Once every starter with a game has locked, the period is over for this
  // lineup: a bye can't be changed any more and isn't a problem to nag about.
  // An EMPTY slot can still be filled by a later game, so it holds the lineup
  // open (the review of 2026-09-27, B-221).
  const allLocked =
    starters.length > 0 && starters.every((r) => r.locked || (r.player !== null && !r.lockAt));
  const status: LineupHealth['status'] = allLocked
    ? 'locked'
    : problems.some((p) => p.code !== 'starter-doubtful')
      ? 'error'
      : problems.length
        ? 'warn'
        : 'ok';
  // The next lock's time is the health bar's own line now (`locksAt`, B-221),
  // said the same way for the simulator and a real league.
  const headline =
    status === 'locked'
      ? 'Lineup locked. Enjoy the games.'
      : status === 'ok'
        ? 'Lineup set.'
        : problems.length === 1
          ? `1 problem. ${problems[0]!.message}`
          : `${problems.length} problems.`;
  return { status, headline, problems, locksAt: firstLock ? iso(firstLock) : null };
}

/** The zone the reader is told times in: theirs, or the league's (B-181). */
function readerZoneOf(state: SimState): string {
  return state.readerZone ?? state.settings.identity.timeZone;
}

export function lineupView(state: SimState, teamId: string, now: number): LineupView {
  const team = state.teams.find((t) => t.id === teamId)!;
  const rows = lineupRows(state, team, now);
  const starters = rows.filter((r) => r.slotKind === 'starter');
  const anyLive = starters.some((r) => r.points !== null);
  return {
    leagueId: state.leagueId,
    teamId,
    team: teamRef(state, team, now),
    period: periodRef(state),
    starters,
    bench: rows.filter((r) => r.slotKind === 'bench'),
    reserve: rows.filter((r) => r.slotKind === 'reserve' || r.slotKind === 'taxi'),
    // No band rule, so the screen offers no IR move here: IR moves are the
    // league store's (D9), and the simulator's rows only read the rule.
    reserveRule: null,
    taxiRule: null,
    health: lineupHealth(state, team, now),
    projectedTotal: round1(starters.reduce((s, r) => s + (r.projection ?? 0), 0)),
    liveTotal: anyLive ? round1(starters.reduce((s, r) => s + (r.points ?? 0), 0)) : null,
    stats: statsOf(state, state.currentPeriod, now),
    bestBall: false,
    eliminatedPeriod: null,
    updatedAt: iso(now),
  };
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

// ── Matchups ──────────────────────────────────────────────────────────────

export function sideScore(state: SimState, team: SimTeam, now: number) {
  const rows = lineupRows(state, team, now).filter((r) => r.slotKind === 'starter');
  const score = round1(rows.reduce((s, r) => s + (r.points ?? 0), 0));
  const projected = round1(
    rows.reduce(
      (s, r) =>
        s + (r.points !== null && r.game?.state === 'final' ? r.points : (r.projection ?? 0)),
      0,
    ),
  );
  // A starter ruled out is not yet to play (B-521); the tour's week has begun.
  const yetToPlay = rows.filter(
    (r) => r.player && r.game?.state === 'pre' && r.health !== 'out',
  ).length;
  const inProgress = rows.filter((r) => r.game?.state === 'live').length;
  return { rows, score, projected, yetToPlay, inProgress };
}

function winProb(
  homeScore: number,
  homeProj: number,
  awayScore: number,
  awayProj: number,
  homeDone: boolean,
  awayDone: boolean,
): number {
  if (homeDone && awayDone) return homeScore > awayScore ? 1 : homeScore < awayScore ? 0 : 0.5;
  const diff = homeProj - awayProj;
  const remaining = Math.max(8, Math.abs(homeProj - homeScore) + Math.abs(awayProj - awayScore));
  const z = diff / (remaining * 0.9);
  return 1 / (1 + Math.exp(-z * 1.7));
}

/**
 * How fresh the simulator's lines are (BACKLOG L2), by the domain's own rule
 * (`statsFreshness`) over its games. Its games ARE its live feed — a line
 * grows through the game at the simulator's own clock — so a game's lines
 * are in once it is over, and the feed polls only while a game is being
 * played: `live` as of now then, `final` as of the last game to finish once
 * every game that has kicked off is over (a Friday, a Saturday, a Sunday
 * morning), and `none` before the first kickoff.
 *
 * It read the period's status until round 3's review (NUMBERS-8, CONTRACT-3):
 * `live` from the first kickoff to the last game's end, so the demo pulsed
 * "Live" on Friday and at "Sunday 12:55" with no game in progress. A past
 * period's games are not kept, so its `asOf` is not known.
 */
export function statsOf(
  state: SimState,
  period: number,
  now: number,
): { state: 'none' | 'delayed' | 'live' | 'final'; asOf: string | null } {
  const status = periodStatus(state, period, now);
  if (status === 'pre') return { state: 'none', asOf: null };
  if (period !== state.currentPeriod || state.proGames.length === 0) {
    return { state: 'final', asOf: null };
  }
  const progress = state.proGames.map((g) => ({ g, p: gameProgress(g, now) }));
  const ended = progress.filter((x) => x.p >= 1).map((x) => x.g.startAt + GAME_SECONDS * 1000);
  const playing = progress.some((x) => x.p > 0 && x.p < 1);
  return statsFreshness({
    now: new Date(now),
    games: progress.map((x) => ({
      start: iso(x.g.startAt),
      status: x.p >= 1 ? 'final' : 'scheduled',
      landed: x.p >= 1,
    })),
    lastFinal: ended.length === 0 ? null : iso(Math.max(...ended)),
    lastLivePoll: playing ? iso(now) : null,
  });
}

export function periodStatus(
  state: SimState,
  period: number,
  now: number,
): 'pre' | 'live' | 'final' {
  if (period < state.currentPeriod) return 'final';
  if (period > state.currentPeriod) return 'pre';
  const starts = state.proGames.map((g) => g.startAt);
  const ends = state.proGames.map((g) => g.startAt + 11700 * 1000);
  if (now < Math.min(...starts)) return 'pre';
  if (now >= Math.max(...ends)) return 'final';
  return 'live';
}

/**
 * The series between two teams (DESIGN.md §2.10 rivalries): past seasons
 * from the remembered head-to-head, this season from the finals, the game
 * being described only once it is final. The line reads from my side when I
 * am in it.
 */
export function rivalryOf(
  state: SimState,
  home: SimTeam,
  away: SimTeam,
  now: number,
): MatchupSummary['rivalry'] {
  let wins = 0;
  let losses = 0;
  let ties = 0;
  let pastStreak = 0;
  for (const season of [...state.history].sort((a, b) => a.season - b.season)) {
    const rec = season.teams.find((t) => t.teamId === home.id)?.vs?.[away.id];
    if (!rec) continue;
    wins += rec.wins;
    losses += rec.losses;
    ties += rec.ties;
    pastStreak =
      rec.streak === 0
        ? pastStreak
        : Math.sign(rec.streak) === Math.sign(pastStreak)
          ? pastStreak + rec.streak
          : rec.streak;
  }
  const results: number[] = [];
  for (let p = 1; p <= state.currentPeriod; p++) {
    const m = state.schedule.find(
      (x) =>
        x.period === p &&
        ((x.home === home.id && x.away === away.id) || (x.home === away.id && x.away === home.id)),
    );
    if (!m || periodStatus(state, p, now) !== 'final') continue;
    const hs = finalScore(state, home.id, p, now);
    const as = finalScore(state, away.id, p, now);
    if (hs === null || as === null) continue;
    if (hs > as) {
      wins++;
      results.push(1);
    } else if (hs < as) {
      losses++;
      results.push(-1);
    } else {
      ties++;
      results.push(0);
    }
  }
  const games = wins + losses + ties;
  // The streak: this season's run, extended by the past when it never broke.
  let streak = 0;
  for (let i = results.length - 1; i >= 0; i--) {
    const r = results[i]!;
    if (r === 0) break;
    if (streak === 0) streak = r;
    else if (Math.sign(r) === Math.sign(streak)) streak += r;
    else break;
  }
  if (streak === 0 && results.length === 0) streak = pastStreak;
  else if (
    streak !== 0 &&
    Math.abs(streak) === results.length &&
    Math.sign(pastStreak) === Math.sign(streak)
  )
    streak += pastStreak;
  const leaderTeamId = wins > losses ? home.id : losses > wins ? away.id : null;
  const streakTeamId = Math.abs(streak) >= 2 ? (streak > 0 ? home.id : away.id) : null;
  const rec = (a: number, b: number) => `${a}–${b}${ties ? `–${ties}` : ''}`;
  let line: string;
  if (games === 0) line = 'First meeting.';
  else if (home.isMine || away.isMine) {
    const meHome = home.isMine;
    const myW = meHome ? wins : losses;
    const theirW = meHome ? losses : wins;
    const them = meHome ? away : home;
    const lead =
      myW > theirW
        ? `You lead ${rec(myW, theirW)}`
        : theirW > myW
          ? `${them.ownerName} leads ${rec(theirW, myW)}`
          : `Series tied ${rec(myW, theirW)}`;
    const n = Math.abs(streak);
    const mineStreak = streak > 0 === meHome;
    const run = streakTeamId ? `; you've ${mineStreak ? 'won' : 'lost'} ${n} straight` : '';
    line = `${lead}${run}`;
  } else {
    const lead =
      wins > losses
        ? `${home.ownerName} leads ${away.ownerName} ${rec(wins, losses)}`
        : losses > wins
          ? `${away.ownerName} leads ${home.ownerName} ${rec(losses, wins)}`
          : `Series tied ${rec(wins, losses)}`;
    const runner = streak > 0 ? home : away;
    const run = streakTeamId ? `; ${runner.ownerName} has won ${Math.abs(streak)} straight` : '';
    line = `${lead}${run}`;
  }
  return { wins, losses, ties, games, leaderTeamId, streakTeamId, streak: Math.abs(streak), line };
}

/** The current starters' season averages, summed: a fair projection for a week not yet set. */
function seasonAverageTotal(state: SimState, team: SimTeam): number {
  return team.lineup
    .filter((row) => row.playerId && slotDefs.get(row.slot)!.kind === 'starter')
    .reduce((sum, row) => sum + (state.players.get(row.playerId!)?.ppg ?? 0), 0);
}

export function matchupSummary(state: SimState, matchupId: string, now: number): MatchupSummary {
  const m = state.schedule.find((x) => x.id === matchupId)!;
  const home = state.teams.find((t) => t.id === m.home)!;
  const away = state.teams.find((t) => t.id === m.away)!;
  const status = periodStatus(state, m.period, now);
  if (m.period !== state.currentPeriod) {
    if (m.period > state.currentPeriod) {
      // A future week: projected from today's starters' averages, so the
      // schedule's win bar and the Pro favorite mean something.
      const hp = round1(seasonAverageTotal(state, home));
      const ap = round1(seasonAverageTotal(state, away));
      return {
        id: m.id,
        period: m.period,
        status,
        home: { team: teamRef(state, home, now), score: 0, projected: hp, yetToPlay: 0 },
        away: { team: teamRef(state, away, now), score: 0, projected: ap, yetToPlay: 0 },
        winProbabilityHome: winProb(0, hp, 0, ap, false, false),
        liveLabel: null,
        kind: matchupKind(state, m),
        rivalry: rivalryOf(state, home, away, now),
        winnerTeamId: null,
        // TODO(Q6): the practice league plays one opponent a week.
        secondGame: false,
      };
    }
    const hs = state.finals.get(`${m.period}:${home.id}`) ?? 0;
    const as = state.finals.get(`${m.period}:${away.id}`) ?? 0;
    return {
      id: m.id,
      period: m.period,
      status,
      home: { team: teamRef(state, home, now), score: hs, projected: hs, yetToPlay: 0 },
      away: { team: teamRef(state, away, now), score: as, projected: as, yetToPlay: 0 },
      winProbabilityHome: status === 'final' ? (hs > as ? 1 : hs < as ? 0 : 0.5) : 0.5,
      liveLabel: null,
      kind: matchupKind(state, m),
      rivalry: rivalryOf(state, home, away, now),
      winnerTeamId:
        status === 'final'
          ? winnerBySimScore(home.id, hs, away.id, as, matchupKind(state, m))
          : null,
      secondGame: false,
    };
  }
  const h = sideScore(state, home, now);
  const a = sideScore(state, away, now);
  const liveGame = state.proGames.find(
    (g) => gameLabel(g, now, readerZoneOf(state)).state === 'live',
  );
  return {
    id: m.id,
    period: m.period,
    status,
    home: {
      team: teamRef(state, home, now),
      score: h.score,
      projected: h.projected,
      yetToPlay: h.yetToPlay,
    },
    away: {
      team: teamRef(state, away, now),
      score: a.score,
      projected: a.projected,
      yetToPlay: a.yetToPlay,
    },
    winProbabilityHome: winProb(
      h.score,
      h.projected,
      a.score,
      a.projected,
      status === 'final',
      status === 'final',
    ),
    liveLabel: liveGame
      ? `${gameLabel(liveGame, now, readerZoneOf(state)).score} · ${gameLabel(liveGame, now, readerZoneOf(state)).label}`
      : null,
    kind: matchupKind(state, m),
    rivalry: rivalryOf(state, home, away, now),
    winnerTeamId:
      status === 'final'
        ? winnerBySimScore(home.id, h.score, away.id, a.score, matchupKind(state, m))
        : null,
    secondGame: false,
  };
}

/**
 * Who a simulated final went to: the higher score; on a level one a bracket
 * game goes to the higher seed, the home side, as `sim/playoffs.ts` advances
 * it, and a regular-season game stays a tie, the presets' default.
 */
function winnerBySimScore(
  homeId: string,
  homeScore: number,
  awayId: string,
  awayScore: number,
  kind: MatchupSummary['kind'],
): string | null {
  if (homeScore > awayScore) return homeId;
  if (awayScore > homeScore) return awayId;
  return kind === 'playoff' || kind === 'consolation' ? homeId : null;
}

function matchupKind(state: SimState, m: SimMatchup): MatchupSummary['kind'] {
  if (m.period <= state.regularSeasonEnd) return 'regular';
  return m.id.startsWith('c') ? 'consolation' : 'playoff';
}

function matchupRows(
  state: SimState,
  team: SimTeam,
  now: number,
  side: 'home' | 'away',
): { rows: MatchupRowView[]; bench: MatchupRowView[]; plays: PlayView[]; benchPoints: number } {
  const all = lineupRows(state, team, now);
  const plays: PlayView[] = [];
  const toRow = (r: LineupRowView): MatchupRowView => {
    let lastDelta: MatchupRowView['lastDelta'] = null;
    if (r.player) {
      const p = state.players.get(r.player.id)!;
      const g = gameFor(state, p);
      const events = state.events.get(p.id) ?? [];
      if (g && now >= g.startAt) {
        const prog = gameProgress(g, now);
        const done = events.filter((e) => e.p <= prog);
        if (done.length) {
          const last = done[done.length - 1]!;
          const before = pointsFor(state, p.id, lineAt(events, last.p - 1e-9));
          const after = pointsFor(state, p.id, lineAt(events, last.p));
          const at = g.startAt + last.p * 11700 * 1000;
          const delta = round1(after - before);
          lastDelta = { points: delta, label: last.description, at: iso(at) };
          if (r.slotKind === 'starter') {
            for (const e of done.slice(-6)) {
              const b = pointsFor(state, p.id, lineAt(events, e.p - 1e-9));
              const aft = pointsFor(state, p.id, lineAt(events, e.p));
              const d = round1(aft - b);
              if (Math.abs(d) >= 0.5) {
                plays.push({
                  id: `${p.id}-${e.p.toFixed(4)}`,
                  at: iso(g.startAt + e.p * 11700 * 1000),
                  playerId: p.id,
                  playerName: p.shortName,
                  side,
                  description: e.description,
                  points: d,
                });
              }
            }
          }
        }
      }
    }
    return {
      slot: r.slot,
      player: r.player,
      game: r.game,
      points: r.points,
      projection: r.projection,
      statLine: r.statLine,
      breakdown: r.breakdown,
      lastDelta,
      basis: r.basis,
      noScore: noScoreOf(r),
      ruledOut: r.health === 'out' && r.game?.state === 'pre',
    };
  };
  const rows = all.filter((r) => r.slotKind === 'starter').map(toRow);
  const benched = all.filter((r) => r.slotKind === 'bench');
  const benchPoints = round1(benched.reduce((s, r) => s + (r.points ?? 0), 0));
  return { rows, bench: benched.map(toRow), plays, benchPoints };
}

/**
 * A side's rows in a week gone by (B-527): the team's lineup that week
 * (`pastLineups`), each player's points that week, the game over. No stat
 * line: the tour keeps none for a week gone by, as its Players rows say (E1).
 */
function pastRows(
  state: SimState,
  team: SimTeam,
  period: number,
): { rows: MatchupRowView[]; bench: MatchupRowView[]; plays: PlayView[]; benchPoints: number } {
  const lineup = state.pastLineups.get(`${period}:${team.id}`) ?? team.lineup;
  const games = state.pastGames.get(period) ?? [];
  // His club's game that week, over (`pastProGames`).
  const over = (p: SimPlayer): GameStatus => {
    const g = games.find((x) => x.home === p.proTeam || x.away === p.proTeam);
    if (!g)
      return {
        state: 'final',
        label: 'Final',
        opponent: null,
        home: false,
        startAt: null,
        score: null,
      };
    const home = g.home === p.proTeam;
    return {
      state: 'final',
      label: 'Final',
      opponent: home ? g.away : g.home,
      home,
      startAt: iso(g.startAt),
      score: gameLabel(g, g.startAt + GAME_SECONDS * 1000 + 1, readerZoneOf(state)).score,
    };
  };
  const toRow = (row: SimTeam['lineup'][number]): MatchupRowView => {
    const p = row.playerId === null ? undefined : state.players.get(row.playerId);
    return {
      slot: row.slot,
      player: p ? playerSummary(state, p) : null,
      game: p ? over(p) : null,
      points: p ? weekPoints(p, period) : null,
      projection: p ? round1(p.ppg) : null,
      statLine: null,
      breakdown: null,
      lastDelta: null,
      basis: null,
      noScore: p ? null : 'empty',
      ruledOut: false,
    };
  };
  const kindOf = (slot: string) => slotDefs.get(slot)?.kind;
  const rows = lineup.filter((row) => kindOf(row.slot) === 'starter').map(toRow);
  const bench = lineup.filter((row) => kindOf(row.slot) === 'bench' && row.playerId).map(toRow);
  return {
    rows,
    bench,
    plays: [],
    benchPoints: round1(bench.reduce((s, r) => s + (r.points ?? 0), 0)),
  };
}

/**
 * Max PF in the tour (B-419's rule, B-527): over the weeks its points for
 * counts, the best lineup its players could have made each week — a week
 * gone by from its details, this week's once it is final from its players'
 * points now — or the score where that is higher.
 */
function maxPointsOf(state: SimState, team: SimTeam, now: number): number | null {
  let sum = 0;
  let weeks = 0;
  for (let period = 1; period <= Math.min(state.currentPeriod, state.regularSeasonEnd); period++) {
    const m = state.schedule.find(
      (x) => x.period === period && (x.home === team.id || x.away === team.id),
    );
    if (!m) continue;
    const score = finalScore(state, team.id, period, now);
    if (score === null) continue;
    const optimal =
      period < state.currentPeriod
        ? (state.periodDetails.get(`${period}:${team.id}`)?.optimal ?? null)
        : bestOf(team.lineup, state.players, (p) => livePoints(state, p, now) ?? 0);
    if (optimal === null) return null;
    sum += Math.abs(optimal - score) < 0.05 ? score : Math.max(optimal, score);
    weeks += 1;
  }
  return weeks === 0 ? null : Math.round(sum * 1000) / 1000;
}

export function matchupView(state: SimState, matchupId: string, now: number): MatchupView {
  const summary = matchupSummary(state, matchupId, now);
  const m = state.schedule.find((x) => x.id === matchupId)!;
  const home = state.teams.find((t) => t.id === m.home)!;
  const away = state.teams.find((t) => t.id === m.away)!;
  // A WEEK GONE BY IS ITS OWN BOX SCORE (B-527): each side's lineup that
  // week with its players' points then, adding up to its final; this
  // week's rows would be somebody else's game.
  const past = m.period < state.currentPeriod;
  const h = past ? pastRows(state, home, m.period) : matchupRows(state, home, now, 'home');
  const a = past ? pastRows(state, away, m.period) : matchupRows(state, away, now, 'away');
  // THE BOT'S LINE (AI_MANAGERS.md §6.5, B-458), chosen as a real league's
  // is: the tour's bots speak on the matchup too.
  const botLineOf = (
    team: SimTeam,
    me: MatchupSummary['home'],
    them: MatchupSummary['home'],
  ): MatchupSide['botLine'] => {
    const member = team.ai === null ? null : castMember(team.ai.presetId);
    if (team.ai === null || member === null) return null;
    const text = matchupLine({
      member,
      trashTalk: team.ai.dials.trashTalk,
      leagueId: state.leagueId,
      teamId: team.id,
      week: summary.period,
      state: summary.status,
      postseason: summary.kind !== 'regular',
      score: me.score,
      theirs: them.score,
      team: me.team.name,
      opponent: them.team.name,
      // A team with no manager's name is called by its own (B-72).
      owner: them.team.ownerName || them.team.name,
    });
    return text === null ? null : { name: team.ai.name, text };
  };
  const side = (
    s: MatchupSummary['home'],
    r: typeof h,
    team: SimTeam,
    other: MatchupSummary['home'],
  ): MatchupSide => ({
    team: s.team,
    score: s.score,
    projected: s.projected,
    yetToPlay: s.yetToPlay,
    adjustments: [],
    inProgress: past ? 0 : sideScore(state, team, now).inProgress,
    rows: r.rows,
    bench: r.bench,
    benchPoints: r.benchPoints,
    botLine: botLineOf(team, s, other),
  });
  const plays = [...h.plays, ...a.plays].sort((x, y) => (x.at < y.at ? 1 : -1)).slice(0, 30);
  return {
    ...summary,
    home: side(summary.home, h, home, summary.away),
    away: side(summary.away, a, away, summary.home),
    plays,
    lastUpdatedAt: iso(now),
    stats: statsOf(state, m.period, now),
    // A level bracket game went to the higher seed (`winnerBySimScore`).
    decidedBy:
      summary.status === 'final' &&
      summary.home.score === summary.away.score &&
      summary.winnerTeamId !== null
        ? 'higher-seed'
        : null,
    // The simulated leagues keep a level game a tie; a bracket game goes to
    // the higher seed, as everywhere.
    tieRule: summary.kind === 'playoff' || summary.kind === 'consolation' ? 'higher-seed' : 'tie',
    margin: summary.status === 'final' ? round1(summary.home.score - summary.away.score) : null,
    // The simulated leagues play no median game.
    median: null,
  };
}

export function scoreboardView(
  state: SimState,
  now: number,
  period = state.currentPeriod,
): ScoreboardView {
  return {
    period: periodRef(state, period),
    field: null,
    out: null,
    matchups: state.schedule
      .filter((m) => m.period === period)
      .map((m) => matchupSummary(state, m.id, now)),
    median: null,
    lastUpdatedAt: iso(now),
    stats: statsOf(state, period, now),
  };
}

export function myMatchupId(state: SimState, period = state.currentPeriod): string | null {
  const mine = state.teams.find((t) => t.isMine)!;
  return (
    state.schedule.find((m) => m.period === period && (m.home === mine.id || m.away === mine.id))
      ?.id ?? null
  );
}

// ── Standings ─────────────────────────────────────────────────────────────

/**
 * A team's final score for a period, or null while that period is still
 * being played. The current period counts as soon as its last game ends, so
 * records and standings move on Tuesday, not next week.
 */
function finalScore(state: SimState, teamId: string, period: number, now: number): number | null {
  if (period < state.currentPeriod) return state.finals.get(`${period}:${teamId}`) ?? 0;
  if (period > state.currentPeriod || periodStatus(state, period, now) !== 'final') return null;
  const team = state.teams.find((t) => t.id === teamId)!;
  return sideScore(state, team, now).score;
}

/**
 * The season's final games between two teams, oldest first, from the first
 * one's side (`HeadToHeadRow`'s `games`, B-418): regular and playoff alike.
 */
export function meetingsOf(
  state: SimState,
  teamId: string,
  opponentId: string,
  now: number,
): {
  matchupId: string;
  period: number;
  kind: 'regular' | 'playoff';
  score: number;
  against: number;
  result: 'W' | 'L' | 'T';
}[] {
  return state.schedule
    .filter(
      (m) =>
        (m.home === teamId && m.away === opponentId) ||
        (m.home === opponentId && m.away === teamId),
    )
    .sort((a, b) => a.period - b.period)
    .flatMap((m) => {
      const score = finalScore(state, teamId, m.period, now);
      const against = finalScore(state, opponentId, m.period, now);
      if (score === null || against === null) return [];
      return [
        {
          matchupId: m.id,
          period: m.period,
          kind: m.period > state.regularSeasonEnd ? ('playoff' as const) : ('regular' as const),
          score,
          against,
          result:
            score > against ? ('W' as const) : score < against ? ('L' as const) : ('T' as const),
        },
      ];
    });
}

/** Every decided matchup for a team: [my score, their score] per period. */
function results(state: SimState, teamId: string, now: number): [number, number][] {
  const out: [number, number][] = [];
  // Standings are the regular season; playoff games decide the bracket, not the record.
  for (let period = 1; period <= Math.min(state.currentPeriod, state.regularSeasonEnd); period++) {
    const m = state.schedule.find(
      (x) => x.period === period && (x.home === teamId || x.away === teamId),
    );
    if (!m) continue;
    const mine = finalScore(state, teamId, period, now);
    if (mine === null) continue;
    const theirs = finalScore(state, m.home === teamId ? m.away : m.home, period, now) ?? 0;
    out.push([mine, theirs]);
  }
  return out;
}

function record(state: SimState, teamId: string, now: number) {
  let wins = 0;
  let losses = 0;
  let ties = 0;
  for (const [mine, theirs] of results(state, teamId, now)) {
    if (mine > theirs) wins++;
    else if (mine < theirs) losses++;
    else ties++;
  }
  return { wins, losses, ties };
}

export function standingsView(state: SimState, now: number): StandingsView {
  const unranked: Omit<
    StandingsRow,
    'gamesBack' | 'pointsBack' | 'seed' | 'results' | 'allPlay' | 'gamesFromLine'
  >[] = state.teams.map((team) => {
    const rec = record(state, team.id, now);
    let pf = 0;
    let pa = 0;
    let streak = '';
    let last: 'W' | 'L' | 'T' | null = null;
    let streakN = 0;
    for (const [mine, theirs] of results(state, team.id, now)) {
      pf += mine;
      pa += theirs;
      const res: 'W' | 'L' | 'T' = mine > theirs ? 'W' : mine < theirs ? 'L' : 'T';
      if (res === last) streakN++;
      else {
        last = res;
        streakN = 1;
      }
    }
    if (last) streak = `${last}${streakN}`;
    return {
      rank: 0,
      team: teamRef(state, team, now),
      wins: rec.wins,
      losses: rec.losses,
      ties: rec.ties,
      pointsFor: round1(pf),
      pointsAgainst: round1(pa),
      streak,
      // Worked out below, once the table is in order.
      playoffStatus: null,
      divisionId: null,
      tiebreakNote: null,
      raceNote: null,
      eliminatedPeriod: null,
      maxPoints: maxPointsOf(state, team, now),
    };
  });
  unranked.sort((a, b) => b.wins - a.wins || b.pointsFor - a.pointsFor);
  unranked.forEach((r, i) => (r.rank = i + 1));

  // WHO HAS CLINCHED, WHO IS OUT, AND WHAT HAS TO HAPPEN (B-397, B-460), by
  // the rules a real league's table runs (`playoffStatuses`, `raceNotes`),
  // from the tour's own records and the regular-season games it has still to
  // play. Until 2026-10-26 the tour said neither (B-524).
  const open = state.schedule.filter(
    (m) => m.period <= state.regularSeasonEnd && finalScore(state, m.home, m.period, now) === null,
  );
  const remaining = new Map<string, number>();
  for (const m of open)
    for (const id of [m.home, m.away]) remaining.set(id, (remaining.get(id) ?? 0) + 1);
  const playoffRules = state.settings.playoffs;
  const statuses = playoffStatuses(
    unranked.map((r, i) => ({
      teamId: r.team.id,
      wins: r.wins,
      losses: r.losses,
      ties: r.ties,
      remaining: remaining.get(r.team.id) ?? 0,
      rank: i + 1,
      divisionId: null,
    })),
    { teamCount: playoffRules.teamCount, seeding: playoffRules.seeding },
    { ranksByRecord: true },
    open.length === 0,
  );
  const openWeeks = new Set(open.map((m) => m.period));
  const lastWeek = openWeeks.size === 1 ? [...openWeeks][0]! : null;
  const race =
    statuses === null || lastWeek === null
      ? null
      : raceNotes(
          unranked.map((r) => ({
            teamId: r.team.id,
            wins: r.wins,
            losses: r.losses,
            ties: r.ties,
          })),
          open.filter((m) => m.period === lastWeek).map((m) => ({ home: m.home, away: m.away })),
          playoffRules.teamCount,
          { ties: (state.settings.tiebreakers.matchup ?? 'tie') === 'tie' },
        );
  // The marks follow the search where it settles a team, as a real league's.
  const marks = statuses === null ? null : new Map(statuses);
  for (const [teamId, settled] of race?.settled ?? []) marks?.set(teamId, settled);
  const nameOf = (teamId: string) =>
    unranked.find((r) => r.team.id === teamId)?.team.name ?? 'another team';
  for (const r of unranked) {
    r.playoffStatus = marks?.get(r.team.id) ?? null;
    const note = race?.notes.get(r.team.id);
    r.raceNote =
      note === undefined || marks?.get(r.team.id) !== 'alive' ? null : raceSentence(note, nameOf);
  }
  // The games the record counts (`results`): the regular season's, finished.
  const finals: TableGame[] = [];
  for (const m of state.schedule) {
    if (m.period > Math.min(state.currentPeriod, state.regularSeasonEnd)) continue;
    const home = finalScore(state, m.home, m.period, now);
    const away = finalScore(state, m.away, m.period, now);
    if (home === null || away === null) continue;
    finals.push({
      period: m.period,
      kind: 'regular',
      homeTeamId: m.home,
      awayTeamId: m.away,
      homeScore: home,
      awayScore: away,
      won: home > away ? 'home' : away > home ? 'away' : 'tie',
    });
  }
  const playoffs = state.settings.playoffs;
  const rows = withPlayoffLine(
    withTableLines(withSeeds(withGamesBack(unranked), playoffs), finals),
    playoffs,
  );
  return {
    throughPeriod: Math.min(state.currentPeriod - 1, state.regularSeasonEnd),
    playoffTeamCount: state.settings.playoffs.teamCount,
    regularSeasonEnd: state.regularSeasonEnd,
    rows,
    divisions: [],
  };
}

// ── Players ───────────────────────────────────────────────────────────────

/**
 * A player's season so far, as the Players list shows it: each played
 * period's points, and their sum to a tenth. One copy, so the lineup's
 * average and the pool's season points are one figure (B-165).
 */
function seasonSoFar(p: SimPlayer, played: number): { weeks: number[]; points: number } {
  const weeks = pastWeekPoints(p, played);
  return { weeks, points: round1(weeks.reduce((sum, w) => sum + w, 0)) };
}

/** Builds a pool row for any player, as the league stands at `now`. */
function rowBuilder(state: SimState, now: number) {
  const mine = state.teams.find((t) => t.isMine)!;
  const ownerOf = new Map<string, SimTeam>();
  for (const t of state.teams) for (const id of t.roster) ownerOf.set(id, t);
  const mySlot = new Map(mine.lineup.filter((r) => r.playerId).map((r) => [r.playerId!, r.slot]));
  const lockedSet = lockedPlayers(state, now);
  const myClaimOn = new Map(
    state.waiverState.claims
      .filter((c) => c.status === 'pending' && c.teamId === mine.id)
      .map((c) => [c.playerId, c.id]),
  );

  const toRow = (p: SimPlayer): PlayerRowView => {
    const owner = ownerOf.get(p.id);
    const g = gameStatus(state, p, now);
    const { ownershipPct, trend } = poolDraws(p);
    // On waivers after a drop or the draft, or because the game has started
    // (PLAN-2026-09-24 §2 rule 6): claimable until the run, not addable.
    const waiverClears = owner ? null : waiverClearsAt(state, p.id, now);
    const availability: PlayerRowView['availability'] = owner
      ? owner.isMine
        ? { kind: 'mine', slot: mySlot.get(p.id) ?? 'BN' }
        : { kind: 'owned', team: teamRef(state, owner, now) }
      : waiverClears
        ? { kind: 'waivers', clearsAt: iso(waiverClears), myClaimId: myClaimOn.get(p.id) ?? null }
        : { kind: 'free-agent' };
    const locked = lockedSet.has(p.id);
    const action: PlayerRowView['action'] =
      availability.kind === 'mine'
        ? locked
          ? 'none'
          : 'drop'
        : availability.kind === 'owned'
          ? 'trade'
          : availability.kind === 'waivers'
            ? 'claim'
            : locked
              ? 'none'
              : 'add';
    const played = Math.max(0, state.currentPeriod - 1);
    const { weeks, points } = seasonSoFar(p, played);
    const lastThree = weeks.slice(-3);
    return {
      player: playerSummary(state, p),
      game: g,
      // TODO(E6): the matchup cue in the tour. The simulator plays one week
      // of pro games, so no club has a season of points allowed to rank.
      matchup: null,
      // The simulator's own ownership and trend stand; nothing counts adds or
      // ranks a position by its projection here. The season rank is the
      // page's, over every row (`playersPage`).
      adds: null,
      posRank: null,
      seasonRank: null,
      watched: false,
      // TODO(E8): the deeper numbers in the tour. Its rows build their
      // season from its own weeks (`seasonSoFar`), not the league store's
      // `seasonFigures`.
      deeper: null,
      // TODO(E1): the stats in the tour. Its weeks are points it makes up
      // (`seasonSoFar`), with no stat line behind them to total.
      stats: null,
      projection: projection(state, p),
      // The simulator's rows carry their number without its basis; the
      // lineup and matchup rows are where the demo explains a projection.
      basis: null,
      seasonPoints: points,
      // The basis's arithmetic (`basisFor`): the season over the weeks played.
      seasonAvg: played > 0 ? Math.round((points / played) * 100) / 100 : null,
      // The contract's "last three games with a line": null before the first.
      lastThreeAvg: lastThree.length
        ? round1(lastThree.reduce((s, w) => s + w, 0) / lastThree.length)
        : null,
      ownershipPct,
      trend,
      availability,
      locked,
      action,
      reason: locked && action === 'none' ? 'Locked until the week ends.' : null,
    };
  };
  return { mine, ownerOf, toRow };
}

/**
 * Who holds each player and what I can do about it, as his pool row says:
 * News's Add and Claim in the tour (B-416), from the row's own rule.
 */
export function poolStanding(
  state: SimState,
  now: number,
): (playerId: string) => Pick<PlayerRowView, 'availability' | 'action' | 'reason'> | null {
  const { toRow } = rowBuilder(state, now);
  return (playerId) => {
    const p = state.players.get(playerId);
    if (p === undefined) return null;
    const r = toRow(p);
    return { availability: r.availability, action: r.action, reason: r.reason };
  };
}

/**
 * The pool's order, as `PlayersQuery` promises it: `@fsp/domain`'s one
 * comparator, which a real league's pool sorts by too.
 */
export { comparePlayerRows };

export function playersPage(state: SimState, query: PlayersQuery, now: number): PlayersPage {
  const { toRow } = rowBuilder(state, now);
  const all = [...state.players.values()].map(toRow);
  // THE SEASON RANK (B-321), over every player at his position, as a real
  // league's pool ranks it.
  const ranks = ranksWithin(
    all.map((r) => ({ id: r.player.id, group: r.player.positions[0], value: r.seasonPoints })),
  );
  let rows = all.map((r) => ({ ...r, seasonRank: ranks.get(r.player.id) ?? null }));
  // WHO CAN FILL A SLOT (B-320), by the domain's one rule. A slot this
  // league's roster does not have lists nobody: the screens ask only for the
  // slots of a lineup, and a real league's server refuses the rest.
  const inRoster =
    query.slot !== undefined &&
    slotsWithReserve(state.settings.roster.slots, state.settings.roster.reserve).some(
      (s) => s.slot === query.slot,
    );
  const def = inRoster ? slotDefs.get(query.slot!) : undefined;
  if (query.slot !== undefined) {
    const reserve = state.settings.roster.reserve;
    rows = rows.filter(
      (r) =>
        def !== undefined &&
        canFillSlot(def, reserve, {
          positions: r.player.positions,
          injuryStatus: r.player.injury?.status ?? null,
        }),
    );
  }
  rows = rows.filter((r) => {
    switch (query.availability) {
      case 'free-agents':
        return r.availability.kind === 'free-agent' || r.availability.kind === 'waivers';
      case 'waivers':
        return r.availability.kind === 'waivers';
      case 'taken':
        return r.availability.kind === 'owned';
      case 'mine':
        return r.availability.kind === 'mine';
      case 'all':
        return true;
      // The tour has no account to keep a watchlist on.
      case 'watching':
        return false;
    }
  });
  if (query.positions.length)
    rows = rows.filter((r) => r.player.positions.some((p) => query.positions.includes(p)));
  // One pro club (E1, B-479), as the store narrows it.
  if (query.club !== undefined) rows = rows.filter((r) => r.player.proTeam === query.club);
  if (query.q) {
    const q = query.q.toLowerCase();
    rows = rows.filter(
      (r) =>
        r.player.name.toLowerCase().includes(q) ||
        (r.player.proTeam ?? '').toLowerCase().includes(q) ||
        // As it reads too: `lar` finds the Rams (B-510).
        (r.player.proTeam === null ? '' : clubLabel(r.player.proTeam)).toLowerCase().includes(q),
    );
  }
  // POINTS A GAME BY DEFAULT, as a real league's pool (B-365): the simulator
  // projects everybody, and a player on his bye still projects nothing.
  const sort = query.sort ?? 'average';
  const stat = sort === 'stat' ? (query.stat ?? null) : null;
  rows.sort(comparePlayerRows(sort, query.reverse, stat));

  const start = query.cursor ? Number(query.cursor) : 0;
  const page = rows.slice(start, start + query.limit);
  return {
    items: page,
    nextCursor: start + query.limit < rows.length ? String(start + query.limit) : null,
    total: rows.length,
    // The practice league's chips are the client's own list (Q11).
    positions: [],
    // Its lineup's own choices, as a real league's pool offers them (B-511).
    filters: positionNumbersFor(football).filters(
      state.settings.roster.slots.flatMap(({ slot, count }) => {
        const d = football.slots.find((s) => s.id === slot);
        return d !== undefined && d.kind === 'starter' && count > 0
          ? [{ id: d.id, eligible: d.eligible }]
          : [];
      }),
    ),
    // Its clubs are the sport's list, which its players' codes are (B-479).
    clubs: football.proTeams
      .map((t) => ({ id: t.id, name: t.name }))
      .sort((x, y) => x.name.localeCompare(y.name)),
    sort,
    slot:
      def === undefined
        ? null
        : { id: def.id, name: def.name, kind: def.kind, positions: [...def.eligible] },
    // No stat tables in the tour: its rows have no lines to total, as the row says.
    stat,
    statGroups: [],
    statWeek: null,
  };
}

/**
 * Worth a look in the practice league (PLAN-2026-10-27-worth-a-look §7): the
 * lists a real league's store makes (`worthLists`, one copy), over the tour's
 * own rows and its weeks gone by, every club playing in them (B-527).
 */
export function worthALookView(state: SimState, now: number): WorthALookView {
  const { toRow } = rowBuilder(state, now);
  const all = [...state.players.values()].map(toRow);
  // THE PLAYERS LIST'S OWN ROWS, its season rank included (`playersPage`).
  const ranks = ranksWithin(
    all.map((r) => ({ id: r.player.id, group: r.player.positions[0], value: r.seasonPoints })),
  );
  const rows = all
    .filter((r) => r.availability.kind === 'free-agent' || r.availability.kind === 'waivers')
    .map((r) => ({ ...r, seasonRank: ranks.get(r.player.id) ?? null }));
  const played = Math.max(0, state.currentPeriod - 1);
  const weeks: { period: number; label: string; key: string }[] = [];
  for (let n = Math.max(1, played - WORTH_WEEKS + 1); n <= played; n++)
    weeks.push({ period: n, label: `Week ${n}`, key: String(n) });
  const past = new Map<string, number[]>();
  const pointsOf = (id: string): number[] => {
    let weekly = past.get(id);
    if (weekly === undefined) {
      const p = state.players.get(id);
      weekly = p === undefined ? [] : pastWeekPoints(p, played);
      past.set(id, weekly);
    }
    return weekly;
  };
  const starting = state.settings.roster.slots.flatMap(({ slot, count }) => {
    const d = football.slots.find((s) => s.id === slot);
    return d !== undefined && d.kind === 'starter' && count > 0
      ? [{ id: d.id, eligible: d.eligible }]
      : [];
  });
  return worthLists({
    rows,
    weeks,
    pointsIn: (id, week) => pointsOf(id)[Number(week) - 1],
    // Every club plays in a week gone by (`pastProGames`).
    played: () => true,
    positions: positionNumbersFor(football)
      .filters(starting)
      .filter((f) => f.positions.length === 1),
    // TODO(E1): the tour's players have no depth chart, so it has no next man up.
    world: [],
    severity: (status) => statuses.get(status)?.severity ?? null,
    summary: (id) => playerSummary(state, state.players.get(id)!),
    seasonAvg: (id) => {
      const weekly = pointsOf(id);
      return weekly.length === 0
        ? null
        : Math.round((weekly.reduce((sum, w) => sum + w, 0) / weekly.length) * 100) / 100;
    },
  });
}

// ── Home ──────────────────────────────────────────────────────────────────

export function leagueRef(state: SimState): LeagueRef {
  const mine = state.teams.find((t) => t.isMine);
  return {
    id: state.leagueId,
    name: state.leagueName,
    sport: state.sport,
    season: state.season,
    status: state.status,
    teamCount: state.teams.length,
    myTeamId: state.joined && mine ? mine.id : null,
    isCommissioner: state.isCommissioner,
    // The simulator's leagues always have somebody running them: the world is
    // generated with a commissioner. It is on the ref for the real store's
    // sake, where a commissioner can delete their account.
    needsCommissioner: false,
    commissionerName: state.commissionerName,
    draftAt: state.draftAt === null ? null : iso(state.draftAt),
    // The simulator's leagues play head-to-head.
    format: HEAD_TO_HEAD,
    tools: toolsRefOf(state.settings),
    // The practice league plays one season (PLAN-2026-10-27-the-next-season §9).
    lastSeasonId: null,
    nextSeasonId: null,
    // The weekly hat, as a real league's week decides it (sim/awards.ts).
    hat: state.settings.fun.hat.enabled ? state.hat : null,
  };
}

/** Members who have actually joined: open seats have no owner yet. */
function joinedCount(state: SimState): number {
  return state.teams.filter((t) => t.ownerName !== '').length;
}

function draftRoomOpen(state: SimState, now: number): boolean {
  return state.draftAt !== null && now >= state.draftAt - 30 * 60 * 1000;
}

/** Pre-draft Home (HOME.md "Pre-draft" state): the draft is the one thing. */
function setupHomeView(state: SimState, now: number): HomeView {
  return {
    league: leagueRef(state),
    field: null,
    fieldOf: null,
    period: periodRef(state),
    health: {
      status: 'ok',
      headline: 'No lineup yet. Draft first, then this is where you set it.',
      problems: [],
      locksAt: null,
    },
    matchup: null,
    secondMatchup: null,
    // Before the draft no game counts for this league.
    stats: { state: 'none', asOf: null },
    attention:
      state.draftAt === null
        ? []
        : [{ kind: 'draft', startsAt: iso(state.draftAt), roomOpen: draftRoomOpen(state, now) }],
    news: [],
    pulse: pulseFromChat(state),
    standingsWindow: [],
    myWeek: null,
    updatedAt: iso(now),
  };
}

export function homeView(state: SimState, now: number): HomeView {
  if (state.status === 'setup') return setupHomeView(state, now);
  const mine = state.teams.find((t) => t.isMine)!;
  const standings = standingsView(state, now);
  const myIndex = standings.rows.findIndex((r) => r.team.id === mine.id);
  const window = standings.rows.slice(Math.max(0, myIndex - 1), Math.max(0, myIndex - 1) + 3);
  const matchupId = myMatchupId(state);
  const pending = pendingClaimsSummary(state, now);

  return {
    league: leagueRef(state),
    field: null,
    fieldOf: null,
    period: periodRef(state),
    health: lineupHealth(state, mine, now),
    matchup: matchupId ? matchupSummary(state, matchupId, now) : null,
    secondMatchup: null,
    stats: statsOf(state, state.currentPeriod, now),
    attention: [
      ...offersForMe(state, now).map((t) => ({
        kind: 'trade-offer' as const,
        tradeId: t.id,
        fromTeam: t.proposer,
        summary: `${t.sides[0].players.map((p) => p.player.name).join(', ')} for your ${t.sides[1].players.map((p) => p.player.name).join(', ')}`,
        // The same fact as `summary`, in a shape that is not a sentence. The
        // copy rule (API.md): a copy field may accompany a fact and never
        // replace it, and `summary` was the sole carrier of what this trade
        // contained until 2026-09-17 (audit 3.7). `give` and `get` are from
        // the RECIPIENT's point of view — side 1 is mine, so what I give is
        // what side 1 offers up.
        players: {
          give: t.sides[1].players.map((p) => p.player),
          get: t.sides[0].players.map((p) => p.player),
        },
        expiresAt: t.respondBy,
      })),
      ...(pending.count > 0 && pending.processesAt !== null
        ? [
            {
              kind: 'waiver-pending' as const,
              count: pending.count,
              processesAt: iso(pending.processesAt),
              budgetLeft: state.settings.waivers.system === 'faab' ? mine.faabRemaining : null,
              budget:
                state.settings.waivers.system === 'faab'
                  ? (state.settings.waivers.faab?.budget ?? null)
                  : null,
            },
          ]
        : []),
    ],
    news: newsItems(state, now, 'mine').slice(0, 5),
    pulse: pulseFromChat(state),
    standingsWindow: window,
    myWeek: myWeek(state, now),
    updatedAt: iso(now),
  };
}

// ── Hub (SITE_HOME.md) ────────────────────────────────────────────────────

/** What one league needs from me right now, most urgent first. */
export function leagueChores(state: SimState, now: number): Chore[] {
  const leagueId = state.leagueId;
  const leagueName = state.leagueName;
  const chores: Chore[] = [];

  if (state.status === 'setup') {
    chores.push({
      kind: 'draft',
      leagueId,
      leagueName,
      startsAt: iso(state.draftAt!),
      roomOpen: draftRoomOpen(state, now),
      joined: joinedCount(state),
      of: state.teams.length,
    });
    return chores;
  }

  const mine = state.teams.find((t) => t.isMine)!;
  const health = lineupHealth(state, mine, now);
  if (health.status === 'error' || health.status === 'warn') {
    const first = health.problems[0]!;
    chores.push({
      kind: 'lineup',
      leagueId,
      leagueName,
      headline: health.headline,
      problemCount: health.problems.length,
      swap:
        first.swap && first.rowSlot !== null && first.rowSlotIndex !== null
          ? {
              candidates: first.swap.candidates,
              rowSlot: first.rowSlot,
              rowSlotIndex: first.rowSlotIndex,
            }
          : null,
      locksAt: health.locksAt,
    });
  }

  const home = homeView(state, now);
  // `attention` is nullable on the contract since E1 — null means a server has
  // not computed it. The simulator always has, so this reads as a list.
  for (const a of home.attention ?? []) {
    if (a.kind === 'trade-offer')
      chores.push({
        kind: 'trade-offer',
        leagueId,
        leagueName,
        tradeId: a.tradeId,
        fromOwner: a.fromTeam.ownerName,
        summary: a.summary,
        players: a.players,
        expiresAt: a.expiresAt,
      });
    else if (a.kind === 'waiver-pending')
      chores.push({
        kind: 'waiver-pending',
        leagueId,
        leagueName,
        count: a.count,
        processesAt: a.processesAt,
      });
  }

  if (home.matchup && home.matchup.status === 'final') {
    const m = home.matchup;
    const me = m.home.team.isMine ? m.home : m.away;
    const them = m.home.team.isMine ? m.away : m.home;
    chores.push({
      kind: 'result',
      leagueId,
      leagueName,
      matchupId: m.id,
      // The winner the matchup recorded, a level playoff game's home side
      // included (`winnerBySimScore`); a tie is a level score with none.
      won: m.winnerTeamId !== null ? m.winnerTeamId === me.team.id : me.score > them.score,
      tied: m.winnerTeamId === null && me.score === them.score,
      margin: round1(Math.abs(me.score - them.score)),
      opponentName: them.team.ownerName,
      // TODO(Q6): the practice league plays one opponent a week.
      second: null,
    });
  }

  return chores.sort((a, b) => CHORE_ORDER[a.kind] - CHORE_ORDER[b.kind]);
}

export function leagueCard(state: SimState, now: number): LeagueCard {
  const mine = state.teams.find((t) => t.isMine)!;
  const inSeason = state.status === 'in-season';
  const matchupId = inSeason ? myMatchupId(state) : null;
  const standings = inSeason ? standingsView(state, now) : null;
  const rank = standings ? standings.rows.findIndex((r) => r.team.id === mine.id) + 1 : 0;
  return {
    league: leagueRef(state),
    // The simulator's leagues play head-to-head.
    field: null,
    fieldOf: null,
    eliminatedPeriod: null,
    live: inSeason && state.proGames.some((g) => now >= g.startAt && now < g.startAt + LIVE_MS),
    myTeam: teamRef(state, mine, now),
    period: inSeason ? periodRef(state) : null,
    matchup: matchupId ? matchupSummary(state, matchupId, now) : null,
    secondMatchup: null,
    stats: matchupId ? statsOf(state, state.currentPeriod, now) : null,
    standing: standings ? { rank, of: standings.rows.length } : null,
    members: { joined: joinedCount(state), of: state.teams.length },
    chores: leagueChores(state, now),
  };
}

/** The hub in one shape: every league's card and every chore merged, most urgent first. */
export function hubView(world: readonly SimState[], now: number): HubView {
  const leagues = world.filter((s) => s.joined).map((s) => leagueCard(s, now));
  // The simulator computes every card's chores, so `?? []` here is exhausting
  // a possibility rather than hiding one: `LeagueCard.chores` is nullable
  // because the league STORE has no chore engine (audit 3.7), and the
  // simulator is the half that does.
  const chores = leagues
    .flatMap((l) => l.chores ?? [])
    .filter((c) => c.kind !== 'waiver-pending')
    .sort((a, b) => CHORE_ORDER[a.kind] - CHORE_ORDER[b.kind]);
  return { user: { displayName: 'Ken' }, leagues, chores, updatedAt: iso(now) };
}
